import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Warehouse as WarehouseIcon, Inventory2, ArrowBack, Search } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import useDebounce from '../../../hooks/useDebounce';
import warehouseService from '../services/warehouseService';
import { extractErrorMessage } from '../../../utils/errorUtils';

const WarehouseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [warehouse, setWarehouse] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1, limit: 10 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [loadingWarehouse, setLoadingWarehouse] = useState(true);
  const [loadingBoxes, setLoadingBoxes] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    warehouseService
      .getById(id)
      .then((res) => {
        if (res?.data) setWarehouse(res.data);
        else setError('Bodega no encontrada');
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoadingWarehouse(false));
  }, [id]);

  useEffect(() => {
    setLoadingBoxes(true);
    warehouseService
      .getAllBoxes({ warehouseId: id, page, limit: 10, search: debouncedSearch || undefined })
      .then((res) => {
        setBoxes(res.data || []);
        setPagination(res.pagination || { total: 0, totalPages: 1, page: 1, limit: 10 });
      })
      .catch(() => {})
      .finally(() => setLoadingBoxes(false));
  }, [id, page, debouncedSearch]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  if (loadingWarehouse) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !warehouse) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Bodega no encontrada'}</Alert>
        <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/warehouses')}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={warehouse.name}
        subtitle={warehouse.company?.name || ''}
        actions={
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/warehouses')}>
            Volver
          </Button>
        }
      />

      {/* Info de la bodega */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WarehouseIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>Información de la Bodega</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Código</Typography>
            <Chip label={warehouse.code} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Empresa</Typography>
            <Typography variant="body1" fontWeight={600}>{warehouse.company?.name || '—'}</Typography>
          </Box>
          {warehouse.address && (
            <Box>
              <Typography variant="body2" color="text.secondary">Dirección</Typography>
              <Typography variant="body1">{warehouse.address}</Typography>
            </Box>
          )}
          {warehouse.email && (
            <Box>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{warehouse.email}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">Total de cajas</Typography>
            <Chip label={pagination.total} size="small" color="info" sx={{ mt: 0.5 }} />
          </Box>
        </Box>
      </Paper>

      {/* Cajas */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2 color="primary" />
          <Typography variant="h6">Cajas</Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Buscar por código..."
          value={search}
          onChange={handleSearchChange}
          sx={{ width: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loadingBoxes ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : boxes.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Inventory2 sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>{search ? 'No se encontraron cajas con esa búsqueda' : 'No hay cajas en esta bodega'}</Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell>Isla</TableCell>
                  <TableCell>Estante</TableCell>
                  <TableCell>Nivel</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {boxes.map((box) => (
                  <TableRow
                    key={box.id}
                    hover
                    onClick={() => navigate(`/warehouses/boxes/${box.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Inventory2 color="primary" fontSize="small" />
                        <strong>{box.code}</strong>
                      </Box>
                    </TableCell>
                    <TableCell>{box.company?.name || '—'}</TableCell>
                    <TableCell>{box.island || '—'}</TableCell>
                    <TableCell>{box.shelf || '—'}</TableCell>
                    <TableCell>{box.level || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination.total > pagination.limit && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={pagination.limit}
              rowsPerPageOptions={[10]}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WarehouseDetail;
